/**
 * Where a half-finished tour lives.
 *
 * A new hire does the daily-report tour between two real jobs, gets called away, and comes back
 * tomorrow. Progress therefore has to survive a reload, and it has to be PER USER — two people share
 * one laptop in a construction office, which is exactly why bid drafts are already user-scoped
 * (modules/bidworkup/hooks/useAutoSaveDraft.js:16-28). Same key shape here.
 *
 * Progress is stored as a STEP ID, never an index. Indices move the moment a step is filtered out by
 * permission or someone inserts one in the middle, and a resume that silently lands two steps early
 * is the kind of wrong that nobody reports as a bug.
 *
 * ── TWO THINGS THAT ARE TRUE TODAY AND SHOULD BE DECIDED, NOT DISCOVERED ─────────────────────────
 * 1. useAuth.logout() (hooks/useAuth.jsx:200-209) removes an explicit list of keys and does NOT
 *    touch `bfs_tour_*`. That is intentional: a tour spanning several sessions must outlive a
 *    logout. Do not "tidy" a removal line in here.
 * 2. AppHeader.handleLogout (shared/components/AppHeader.jsx:45) calls localStorage.clear(), which
 *    is the logout button users actually press, and it wipes tour progress (and tenant_branding)
 *    along with everything else.
 *
 *    ── INVESTIGATED, AND DELIBERATELY LEFT ALONE ──────────────────────────────────────────────
 *    `localStorage.clear()` has exactly ONE call site in src/ (that line; the other two hits are
 *    this comment and the vitest setup file), and it runs ONLY on logout. So what it costs is
 *    progress ACROSS A LOGOUT — not across a reload and not across a route change, both of which
 *    are covered by tests (__tests__/tourEngine.test.jsx "resumes an interrupted tour…" remounts
 *    the whole tree; __tests__/tourGating.test.jsx "survives a route change…" navigates mid-tour
 *    and checks the stored step id afterwards).
 *
 *    What the clear() is FOR is a shared office laptop: the next person to log in on that machine
 *    must not inherit the last one's cached user, tenant branding, or bid drafts (which carry
 *    confidential pricing — see the F-L4 note in useAuth.logout, which removes the same things by
 *    an explicit list). The proper fix is to route AppHeader's logout through useAuth().logout()
 *    so there is one logout path instead of two, and that remains the right change — but it is a
 *    change to the SESSION path, and buying "a tour resumes across a logout" with a risk of
 *    mis-logging people out is a bad trade. Snapshotting the bfs_tour_* keys around the clear()
 *    would be the workaround, and a workaround is worse than the gap. Leave both alone.
 */
import { APP_STORAGE_PREFIX } from '../../config/config.js'

const KEY_ROOT = `${APP_STORAGE_PREFIX}_tour`

/**
 * Anonymous (or a user record with no id) gets NO key rather than a shared one. A single global
 * key on a shared device would hand one person's progress to the next person who logs in.
 */
export function progressKey(userId, tourId) {
  if (userId == null || userId === '' || !tourId) return null
  return `${KEY_ROOT}_${String(userId)}_${tourId}`
}

export function readProgress(userId, tourId) {
  const key = progressKey(userId, tourId)
  if (!key) return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // A hand-edited or half-written value must not throw on every render forever.
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch {
    return null
  }
}

export function writeProgress(userId, tourId, patch) {
  const key = progressKey(userId, tourId)
  if (!key) return
  try {
    const next = { ...(readProgress(userId, tourId) || {}), ...patch, updatedAt: Date.now() }
    localStorage.setItem(key, JSON.stringify(next))
  } catch {
    // Quota exceeded, Safari private mode, storage disabled by policy. Losing the resume point is a
    // small loss; a help feature that throws mid-tour and takes the page with it is a large one.
  }
}

export function clearProgress(userId, tourId) {
  const key = progressKey(userId, tourId)
  if (!key) return
  try { localStorage.removeItem(key) } catch { /* see writeProgress */ }
}

/**
 * Turn a stored step id into a starting index in the CURRENTLY VISIBLE steps.
 *
 * Visible-step filtering is recomputed on every launch, so the saved step may no longer be in the
 * list — the user's permissions changed, or the step was deleted in a release. Falling back to 0
 * makes them re-walk a few steps, which is recoverable; guessing an index would drop them somewhere
 * arbitrary, which is not.
 */
export function resumeIndex(steps, progress) {
  if (!progress || progress.completedAt) return 0
  const i = steps.findIndex((s) => s.id === progress.stepId)
  return i >= 0 ? i : 0
}
